import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

const PageTitleContext = createContext(null);

const PageTitleProvider = ({
    children,
    defaultTitle = "Parent Hub",
    defaultDesc = "",
}) => {
    const [title, setTitle] = useState(defaultTitle);
    const [desc, setDesc] = useState(defaultDesc);

    const setPageTitle = useCallback(
        (nextTitle, nextDesc) => {
            setTitle(nextTitle || defaultTitle);
            setDesc(nextDesc || defaultDesc);
        },
        [defaultTitle, defaultDesc],
    );

    const value = useMemo(
        () => ({ title, desc, setPageTitle, defaultTitle, defaultDesc }),
        [title, desc, setPageTitle, defaultTitle, defaultDesc],
    );

    return (
        <PageTitleContext.Provider value={value}>
            {children}
        </PageTitleContext.Provider>
    );
};

const usePageTitleContext = () => {
    const context = useContext(PageTitleContext);
    if (!context) {
        throw new Error(
            "usePageTitleContext must be used within PageTitleProvider",
        );
    }

    return context;
};

const usePageTitle = (nextTitle, nextDesc) => {
    const { setPageTitle, defaultTitle, defaultDesc } = usePageTitleContext();

    useEffect(() => {
        if (!nextTitle) {
            return undefined;
        }

        setPageTitle(nextTitle, nextDesc);

        return () => {
            setPageTitle(defaultTitle, defaultDesc);
        };
    }, [nextTitle, nextDesc, setPageTitle, defaultTitle, defaultDesc]);

    return { setPageTitle };
};

export { PageTitleProvider, usePageTitleContext, usePageTitle };
